import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react"
import queryString from "query-string"

import { IROH_SERVICES_API_PREFIX } from "@/api/domain"
import { Tag } from "@/api/tags"
import { connectWs } from "@/api/ws"
import type { RegionName } from "@/infra"
import type { Interval } from "@/metrics"
import type {
  DeployDomainKind,
  DeployDomainRef,
  DeployInfo,
  ProjectIdParam,
  Uuid,
} from "@/types"
import { formatDateWithoutMillsecondsAndZ } from "@/util"

type RelayTimeseriesParams = {
  // Relay deploy IDs to filter by; the wire query param stays `relays`.
  relays?: Uuid[]
  from: Date // EARLIEST point in time
  to: Date // LATEST point in time
  interval: Interval // interval of time between each data point
}

function relayTimeseriesQuery(p: RelayTimeseriesParams) {
  return queryString.stringify(
    {
      from: formatDateWithoutMillsecondsAndZ(p.from),
      to: formatDateWithoutMillsecondsAndZ(p.to),
      bucket: p.interval,
      relays: p.relays,
    },
    {
      arrayFormat: "bracket",
    },
  )
}

type RelaySample = {
  timestamp: number
  value: number
}

type MachineHealthSample = {
  metric: {
    instance: string
    deploy_id?: string
  }
  sample: {
    timestamp: number
    value: number
  }
}

type MachineHealthSamples = Record<string, MachineHealthSample[]>

type MachineStats = {
  rxByInstance: number
  txByInstance: number
  rxTotal: number
  txTotal: number
  rx: number
  tx: number
}

// Each series is optional: the backend fans out four Prometheus queries in
// parallel and returns whichever ones succeed. A single failing series no
// longer fails the whole request — consumers MUST handle missing keys as
// "no data" (e.g. via `data?.Tx?.[0] ?? []`) rather than asserting presence.
export type HostSeriesStats = {
  Rx?: RelaySample[][]
  Tx?: RelaySample[][]
  // Egress bytes per bucket; a cumulative sum is the window's total egress.
  TxTotal?: RelaySample[][]
  RxByInstance?: RelaySample[][]
  TxByInstance?: RelaySample[][]
}

type PrometheusTotal = { timestamp: number; value: number }[][]

// Per-project relay settings. Mirrors `svc_model::project_relay_settings::RelaySettings`.
export type RelaySettings = {
  access_control_enabled: boolean
}

// One relay URL a project can use, with enough context to render it honestly.
// Mirrors `svc-model::relay_info::RelayInfo`.
export type RelayInfo = {
  host: string
  // Full `https://` URL, ready to paste into a config.
  url: string
  region: RegionName
  tenancy: "dedicated" | "shared"
  // How the name was produced, which is what `primaryRelayUrl` ranks by.
  kind: DeployDomainKind
  // Whether the relay answers on this name today. A v1 relay binds a single
  // hostname, so its other generated names are `not_served` until it upgrades.
  status: "live" | "not_served"
  deploy_id: string | null
  version: string | null
}

// A dedicated relay: its deploy/operational fields (flattened from
// `DeployInfo`) plus its URLs, each carrying whether the relay answers on it.
export type DedicatedRelay = DeployInfo & {
  urls: RelayInfo[]
}

// The one URL to show for a relay, or to hand out in a code snippet: only a
// name it answers on today, preferring the customer's own domain, then the
// project alias, which survives the relay being redeployed. The per-deploy
// name is the last resort — it dies with the deploy. Every surface that has to
// name a relay goes through here, so the dashboard cannot show one address
// while the snippet copies another.
export function primaryRelayUrl(urls: RelayInfo[]): RelayInfo | undefined {
  const live = urls.filter((url) => url.status === "live")
  return (
    live.find((url) => url.kind === "custom") ??
    live.find((url) => url.kind === "environment_id_v1") ??
    live[0]
  )
}

// A shared region the enrollment reconcile has not served yet: enough for a
// placeholder row (the region, and the assigned relay's version when the
// assignment already names one).
export type PendingSharedRelay = {
  region: RegionName
  version: string | null
}

// Every relay a project can use, in one response: its dedicated relays plus the
// shared (managed-fleet) relays it is enrolled on. Usage figures ride a
// separate request (`relayUsageGet`), joined by deploy id, so a slow Prometheus
// cannot stall the list.
export type RelayList = {
  dedicated: DedicatedRelay[]
  shared: RelayInfo[]
  pending_shared: PendingSharedRelay[]
}

// One tenant's own load on one relay, dedicated and shared rows alike. From the
// per-tenant series for a v2 relay, from the box counters for a legacy
// dedicated one. Egress only, like everything that displays or bills transfer.
export type TenantRelayUsage = {
  egress_bytes: number
  connections: number
}

// Per-relay usage keyed by deploy id, covering dedicated and shared relays.
export type RelayUsageMap = Record<string, TenantRelayUsage>

// One live tick's figures for one relay. Mirrors
// `backend::prometheus::LiveRelayPoint`.
export type LiveRelayPoint = {
  connections: number
  // Relayed egress in bytes per second.
  egress_rate: number
  // Relayed egress bytes since the current wall-clock bucket began. Absent
  // when the request carried no bucket size.
  bucket_egress?: number
}

// One live tick for every covered relay, keyed by relay deploy id. Mirrors
// `backend::handlers::relays::LiveRelayStats`.
export type LiveRelayStats = {
  // The tick's evaluation instant in milliseconds, for bucketing the values.
  at_ms: number
  relays: Record<string, LiveRelayPoint>
}

// Mirrors `backend::prometheus::PerRelayStat`.
export enum PerRelayStat {
  Connections = "connections",
  EgressRate = "egress_rate",
  EgressTotal = "egress_total",
}

// One chart series per relay, keyed by relay deploy id.
export type PerRelaySeries = Record<string, RelaySample[]>

// GET /settings response: the tunable config (flattened) plus the shared-relay
// fields an enrolled project needs (it owns no relay deploy, so the
// registered-relay surface is otherwise empty). No usage numbers: those live on
// the relays page, which charts shared and dedicated relays alike. The PUT body
// stays `RelaySettings`.
export type RelaySettingsResponse = RelaySettings & {
  multitenant_enrolled: boolean
  // One URL per region the project is served from. Empty until enrolled and the
  // relays are deployed with published DNS.
  shared_relays: RelayInfo[]
}

// Result of saving settings: the stored config + how many registered
// relays applied the push now (the rest reconcile on reconnect).
export type SaveSettingsResult = {
  settings: RelaySettings
  total_relays: number
  applied: number
}

// One relay registered to this project, keyed by its instance. The slot is
// opened at provision time; `trusted_endpoint_id` and the `*_seen_at`
// timestamps fill in once the relay completes its first backhaul registration.
export type RegisteredRelay = {
  instance_id: string
  project_id: string
  trusted_endpoint_id: string | null
  created_at: string
  first_seen_at: string | null
  region: RegionName
  // Every hostname the relay deploy serves, canonical one first. Empty until
  // the relay has a deploy row.
  domains: DeployDomainRef[]
}

const settingsTagId = (project_id: string) => `settings:${project_id}`
const registeredTagId = (project_id: string) => `registered:${project_id}`

// Define a service using a base URL and expected endpoints
export const relaysApi = createApi({
  reducerPath: "relays",
  tagTypes: [Tag.Relays],
  baseQuery: fetchBaseQuery({
    baseUrl: `${IROH_SERVICES_API_PREFIX}relays`,
    mode: "cors",
    credentials: "include",
  }),
  endpoints: (build) => ({
    relaysGet: build.query<RelayList, ProjectIdParam>({
      query: (params) => ({
        url: `${params.project_id}`,
      }),
      providesTags: (_result, _error, params) => [
        { type: Tag.Relays, id: params.project_id },
      ],
    }),
    // The relay table's usage figures, keyed by deploy id. A separate request
    // from relaysGet so the table renders before Prometheus answers.
    relayUsageGet: build.query<RelayUsageMap, ProjectIdParam>({
      query: (params) => ({
        url: `${params.project_id}/usage`,
      }),
    }),
    // One series per relay for the charts' per-relay mode ("Combined" off).
    relayPerRelaySeriesGet: build.query<
      PerRelaySeries,
      ProjectIdParam & RelayTimeseriesParams & { stat: PerRelayStat }
    >({
      query: (params) => ({
        url: `${params.project_id}/per-relay-series?stat=${params.stat}&${relayTimeseriesQuery(
          params,
        )}`,
      }),
    }),
    relayConnectionsGet: build.query<
      RelaySample[],
      ProjectIdParam & RelayTimeseriesParams
    >({
      query: (params) => ({
        url: `${params.project_id}/connections?${relayTimeseriesQuery(params)}`,
      }),
      transformResponse: (
        baseQueryReturnValue: PrometheusTotal,
        _meta,
        _arg,
      ) => {
        return baseQueryReturnValue[0]
      },
    }),
    // One live tick for every covered relay, keyed by deploy id. Polled by the
    // relay page; a single response updates the charts' newest bucket, the
    // table's endpoint counts, and the summary numbers.
    // `interval` is optional: passing it buys each relay's so-far egress
    // within the current wall-clock bucket; gauge-only readers leave it off.
    relayLiveGet: build.query<
      LiveRelayStats,
      ProjectIdParam & { relays?: Uuid[]; interval?: Interval }
    >({
      query: ({ project_id, relays, interval }) => ({
        url: `${project_id}/live?${queryString.stringify(
          { relays, bucket: interval },
          {
            arrayFormat: "bracket",
          },
        )}`,
      }),
    }),

    // Returns time-series data for the given hosts
    relayGetHostSeries: build.query<
      HostSeriesStats,
      ProjectIdParam & RelayTimeseriesParams
    >({
      query: (params) => ({
        url: `${params.project_id}/host-series?${relayTimeseriesQuery(params)}`,
      }),
    }),

    // Returns the last sample for the given hosts
    relayGetHostStats: build.query<
      Record<string, MachineStats>,
      ProjectIdParam & RelayTimeseriesParams
    >({
      query: (params) => ({
        url: `${params.project_id}/host-stats?${relayTimeseriesQuery(params)}`,
      }),
      transformResponse: (
        baseQueryReturnValue: MachineHealthSamples,
        _meta,
        _arg,
      ) =>
        Object.keys(baseQueryReturnValue).reduce(
          (acc, stat) => {
            for (const value of baseQueryReturnValue[stat]) {
              const deployKey = value.metric.deploy_id ?? value.metric.instance
              if (!acc[deployKey]) {
                acc[deployKey] = {
                  rxByInstance: -1,
                  txByInstance: -1,
                  rxTotal: -1,
                  txTotal: -1,
                  rx: -1,
                  tx: -1,
                }
              }
              // lowercase first letter of stat name
              const statKey = stat.charAt(0).toLowerCase() + stat.slice(1)
              acc[deployKey][statKey as keyof MachineStats] = value.sample.value
            }
            return acc
          },
          {} as Record<string, MachineStats>,
        ),
    }),

    relaySettingsGet: build.query<RelaySettingsResponse, ProjectIdParam>({
      query: ({ project_id }) => ({ url: `${project_id}/settings` }),
      providesTags: (_result, _error, { project_id }) => [
        { type: Tag.Relays, id: settingsTagId(project_id) },
      ],
    }),
    relaySettingsPut: build.mutation<
      SaveSettingsResult,
      ProjectIdParam & { body: RelaySettings }
    >({
      query: ({ project_id, body }) => ({
        url: `${project_id}/settings`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_result, _error, { project_id }) => [
        { type: Tag.Relays, id: settingsTagId(project_id) },
      ],
    }),

    relayRegisteredGet: build.query<RegisteredRelay[], ProjectIdParam>({
      query: ({ project_id }) => ({ url: `${project_id}/registered` }),
      providesTags: (_result, _error, { project_id }) => [
        { type: Tag.Relays, id: registeredTagId(project_id) },
      ],
    }),

    // Same initial payload as relayRegisteredGet, but holds a websocket open
    // and merges each relay live as it authenticates over backhaul (the
    // backend pushes one enriched RegisteredRelay per registration).
    relayRegisteredGetWs: build.query<RegisteredRelay[], ProjectIdParam>({
      query: ({ project_id }) => ({ url: `${project_id}/registered` }),
      async onCacheEntryAdded(
        args,
        { updateCachedData, cacheDataLoaded, cacheEntryRemoved },
      ) {
        await cacheDataLoaded
        const cleanup = connectWs(
          `${IROH_SERVICES_API_PREFIX}relays/${args.project_id}/registered/ws/`,
          (event: MessageEvent) => {
            const relay: RegisteredRelay = JSON.parse(event.data)
            updateCachedData((draft) => {
              const idx = draft.findIndex(
                (r) => r.instance_id === relay.instance_id,
              )
              if (idx >= 0) {
                draft[idx] = relay
              } else {
                draft.push(relay)
              }
            })
          },
        )
        await cacheEntryRemoved
        cleanup()
      },
    }),
  }),
})

export const {
  useRelaysGetQuery,
  useRelayUsageGetQuery,
  useRelayPerRelaySeriesGetQuery,
  useRelayConnectionsGetQuery,
  useRelayLiveGetQuery,
  useRelayGetHostStatsQuery,
  useRelayGetHostSeriesQuery,
  useRelaySettingsGetQuery,
  useRelaySettingsPutMutation,
  useRelayRegisteredGetQuery,
  useRelayRegisteredGetWsQuery,
} = relaysApi
